<script setup lang="ts">
import { computed, ref, defineAsyncComponent } from "vue";
import { useMediaQuery } from "@vueuse/core";
import { Cog6ToothIcon } from "@heroicons/vue/24/outline";
import NavUser from "@/components/NavUser.vue";
import CreateMenu from "@/layouts/frame/CreateMenu.vue";
import RecentItemsList from "@/layouts/frame/RecentItemsList.vue";
import { visibleAreas } from "@/navigation/areas";
import { areaEntry } from "@/navigation/areaSpots";
import { useAuthStore } from "@/stores/authStore";
import { isGateOpen } from "@/navigation/gates";
import { useNavUser } from "@/composables/useNavUser";
import ChangelogFeed from "@/components/Changelog/ChangelogFeed.vue";

// The icon strip is always there, so Home no longer forces the section sidebar
// open: hiding it is remembered (SAU-437).
/**
 * On a phone the icon strip is out of reach, so Home carries what it holds:
 * Create New, Settings and the account menu (with Log out) along the top, and
 * the recent items under the heading. Mounted only on a phone, because
 * Create New listens for the C key and the strip's copy does on a desktop.
 */
const isPhone = useMediaQuery("(max-width: 767.98px)");
const settingsArea = computed(() => visibleAreas(isGateOpen).find((area) => area.id === "settings") ?? null);
const user = useNavUser();
const authStore = useAuthStore();

/**
 * The hidden game behind the "What's new" heading.
 *
 * The `import()` below MUST stay a literal static string. Routing it through a variable or a
 * barrel file makes the bundler give up on splitting and silently folds the whole game — and
 * hls.js behind it — into the dashboard chunk that every user downloads. Nothing fails visibly
 * when that happens; only the e2e assertion that the chunk is fetched on the click catches it.
 */
const GuessTheTrackDialog = defineAsyncComponent(
  () => import("@/components/GuessTheTrack/GuessTheTrackDialog.vue"),
);

// `hasOpenedGame` latches so the component isn't torn out of the tree on close and re-fetched on
// the next open; `gameOpen` drives the dialog itself. `v-if` on the latch is what keeps the
// chunk unfetched until the first click — defineAsyncComponent loads on first render, not on
// definition.
const hasOpenedGame = ref(false);
const gameOpen = ref(false);

function openGame(): void {
  hasOpenedGame.value = true;
  gameOpen.value = true;
}
</script>

<template>
  <!--
    Fill the column and scroll internally, rather than growing it.

    SidebarInset's inner wrapper is the scroll container AND it holds the audio
    player slot, so a page that simply gets taller pushes the player down the
    document and it scrolls away with the content — which is what the dashboard
    did once the changelog gave it real height. Every page where the player
    stays docked does this instead: `min-h-0 flex-1` to claim the column, and
    the overflow on an inner element.
  -->
  <div class="flex min-h-0 flex-1 flex-col">
    <div v-if="isPhone" class="flex shrink-0 items-center justify-between px-3 pt-2" data-testid="home-phone-bar">
      <CreateMenu />
      <div class="flex items-center gap-1">
        <router-link
          v-if="settingsArea"
          :to="areaEntry(settingsArea, authStore.userData?.id)"
          aria-label="Settings"
          data-testid="home-phone-settings"
          class="flex size-10 items-center justify-center rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Cog6ToothIcon class="size-5" aria-hidden="true" />
        </router-link>
        <NavUser :user="user" compact />
      </div>
    </div>
    <div class="min-h-0 flex-1 overflow-y-auto p-6 md:p-10 max-md:pb-mobile-area-bar-clearance">
      <div class="mx-auto flex max-w-3xl flex-col gap-8">
        <!-- No search box and no visible heading here. The old heading asked a
             question that the search box answered, and the box is gone: search is the round Search
             button on a phone, the magnifier in the icon strip on a desktop, and ⌘K
             anywhere. The page still names itself for screen readers. -->
        <h1 class="sr-only">Home</h1>
        <nav v-if="isPhone" aria-label="Recent" class="flex flex-col gap-3" data-testid="home-phone-recents">
          <RecentItemsList />
        </nav>
        <ChangelogFeed @title-click="openGame" />
      </div>
    </div>

    <GuessTheTrackDialog v-if="hasOpenedGame" v-model:open="gameOpen" />
  </div>
</template>
